import React, { useState, useEffect } from 'react';

const RetirementPlanner = () => {
  const [inputs, setInputs] = useState({
    // 생활비 관련
    preRetirementExpenses: 400,    // 은퇴 전 생활비 (월)
    postRetirementExpenses: 300,   // 은퇴 후 생활비 (월)
    inflationRate: 1.9,            // 물가상승률 (%)
    
    // 나이 관련
    currentAge: 50,                // 현재 나이
    retirementAge: 60,             // 은퇴 나이

    // 자산 관련
    realEstate: 50000,                 // 부동산
    insuranceSavings: 3000,        // 저축성예금
    savingsInterest: 2,          // 예금이자 (%)
    domesticStocks: 2000,             // 국내주식
    foreignStocks: 500,           // 해외주식
    dividend: 20,                 // 배당금 (월)
    annualReturn: 1.0,             // 주식 성장률 (%)

    // 수입 관련
    annualIncome: 5000,           // 급여 수입 (연)
    privatePension: 0,             // 퇴직/개인연금 (월)
    privatePensionStartAge: 0,    // 퇴직/개인연금 개시 나이
    nationalPension: 100,          // 국민연금 (월)
    housingPension: 0,             // 주택연금 (월)
    housingPensionStartAge: 65,    // 주택연금 수령 나이
    extraIncome: 50,              // 부수입 (월)
  });

  const [educationExpense, setEducationExpense] = useState({
    monthlyAmount: 100,  // 교육비 (월)
    endAge: 60            // 교육비 종료 나이
  });

  const [otherExpenses, setOtherExpenses] = useState([]); // 나중에 추가 기능 (기타지출)이 있을 경우 사용
  const [planData, setPlanData] = useState([]);
  const [warning, setWarning] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [viewMode, setViewMode] = useState('card'); // 'auto', 'card', 'table'

  useEffect(() => {
    setWarning('');
    setSuccessMessage('');
  }, [inputs]);

  // 컴포넌트 마운트 시 카드뷰 기본값 확실히 설정
  useEffect(() => {
    setViewMode('card');
  }, []);

  useEffect(() => {
    calculatePlan();
    // eslint-disable-next-line
  }, [inputs, educationExpense]); // inputs, educationExpense 변경 시마다 재계산

  const calculatePlan = () => {
    const data = [];

    // 초기 자산 총합
    let totalAssets = 
      inputs.realEstate + 
      inputs.insuranceSavings +
      inputs.domesticStocks + 
      inputs.foreignStocks;

    // 매년 업데이트될 자산 값 (주식, 예금)
    let stocksBalance = inputs.foreignStocks;
    let domesticStocksBalance = inputs.domesticStocks;
    let savingsBalance = inputs.insuranceSavings;

    let zeroBalanceFound = false;
    
    // 초기 생활비
    let currentPreRetirementExpenses = inputs.preRetirementExpenses;
    let currentPostRetirementExpenses = inputs.postRetirementExpenses;

    // 초기 연금
    let currentNationalPension = inputs.nationalPension;
    let currentHousePension = inputs.housingPension;
    let currentPrivatePension = inputs.privatePension;

    for (let age = inputs.currentAge; age <= 110; age++) {
      // 1. 자산 성장 (한 해가 지났을 때 적용)
      if (age > inputs.currentAge) {
        stocksBalance *= (1 + inputs.annualReturn / 100);
        domesticStocksBalance *= (1 + inputs.annualReturn / 100);
        savingsBalance *= (1 + inputs.savingsInterest / 100);
      }

      // 2. 연간 수입 계산
      const annualSalary = age < inputs.retirementAge ? inputs.annualIncome : 0;

      // 연금 (국민연금 + 주택연금 + 퇴직/개인연금)
      const annualNationalPension = age >= 65 ? (currentNationalPension * 12) : 0;
      const annualHousePension = 
        age >= inputs.housingPensionStartAge ? (currentHousePension * 12) : 0;
      const annualPrivatePension = 
        age >= inputs.privatePensionStartAge ? (currentPrivatePension * 12) : 0;
      const totalPension = annualNationalPension + annualHousePension + annualPrivatePension;
      
      // 해마다 연금에 물가상승률 적용
      if (age >= 65) {
        currentNationalPension *= (1 + inputs.inflationRate / 100);
      }
      if (age >= inputs.housingPensionStartAge) {
        currentHousePension *= (1 + inputs.inflationRate / 100);
      }
      if (age >= inputs.privatePensionStartAge) {
        currentPrivatePension *= (1 + inputs.inflationRate / 100);
      }
      // 부수입 + 배당금
      const annualExtraIncome = inputs.extraIncome * 12;
      const annualDividend = inputs.dividend * 12;

      const totalAnnualIncome = annualSalary + totalPension + annualExtraIncome + annualDividend;

      // 3. 연간 지출 계산
      // 생활비(매년 물가상승률 적용)
      if (age >= inputs.currentAge + 1) {
        currentPreRetirementExpenses *= (1 + inputs.inflationRate / 100);
        currentPostRetirementExpenses *= (1 + inputs.inflationRate / 100);
      }

      const annualLivingExpenses =
        age < inputs.retirementAge
          ? currentPreRetirementExpenses * 12
          : currentPostRetirementExpenses * 12;

      // 교육비 (endAge 이하일 때만)
      const annualEducation = age <= educationExpense.endAge 
        ? educationExpense.monthlyAmount * 12 
        : 0;

      // 기타 지출 (해당 나이에 맞춰 추가 가능한 로직)
      const otherExpense = otherExpenses.find(e => e.age === age)?.amount || 0;
      
      // 총 지출
      const totalExpenses = annualLivingExpenses + annualEducation + otherExpense;

      // 4. 현재 총 자산 계산
      totalAssets = totalAssets + totalAnnualIncome - totalExpenses;

      data.push({
        age,
        monthlyExpenses: Math.round(annualLivingExpenses),
        educationExpenses: annualEducation,
        otherExpenses: otherExpense,
        totalExpenses,
        salary: annualSalary,
        pension: totalPension,
        extraIncome: annualExtraIncome,
        dividend: annualDividend,
        realEstate: inputs.realEstate,
        foreignStocks: Math.round(stocksBalance),
        domesticStocks: Math.round(domesticStocksBalance),
        savingsBalance: Math.round(savingsBalance),
        totalBalance: Math.round(totalAssets),
        monthlyCashFlow: Math.round(totalAnnualIncome - totalExpenses)
      });

      if (totalAssets <= 0) {
        setWarning(`은퇴 후 ${age}세에 잔고가 0원이 됩니다.`);
        zeroBalanceFound = true;
        break;
      }
    }

    if (!zeroBalanceFound) {
      setSuccessMessage('시스템 내 최대나이(110세)까지도 자산이 충분히 준비되었습니다');
    }
    
    setPlanData(data);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputs(prev => ({
      ...prev,
      [name]: parseFloat(value) || 0
    }));
  };

  // 금액을 읽기 쉽게 포맷팅하는 함수
  const formatAmount = (amount) => {
    if (amount === 0) return '0';
    
    const absAmount = Math.abs(amount);
    
    // 억원 단위로 변환 (10,000만원 = 1억원)
    if (absAmount >= 10000) {
      const billions = absAmount / 10000;
      if (billions >= 100) {
        return `${amount < 0 ? '-' : ''}${Math.round(billions).toLocaleString()}억원`;
      } else {
        return `${amount < 0 ? '-' : ''}${billions.toFixed(1)}억원`;
      }
    }
    // 만원 단위 그대로 표시
    else {
      return `${Math.round(amount).toLocaleString()}만원`;
    }
  };

  return (
<div className="p-2 sm:p-4 lg:p-8 bg-gradient-to-br from-blue-50 to-indigo-50 min-h-screen">
  <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6 lg:space-y-8">
    {/* 제목 섹션 */}
    <div className="text-center mb-4 sm:mb-6 lg:mb-8">
      <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-2">은퇴 설계 플래너</h1>
      <p className="text-sm sm:text-base text-gray-600 mb-4">
        배포 : 네이버 블로그 -
        <a
          href="https://blog.naver.com/world-response"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-500 hover:underline"
        >
          <strong> '장실장의 인사이드 베트남'</strong>
        </a>
      </p>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 max-w-4xl mx-auto">
        <div className="flex items-start space-x-2">
          <span className="text-blue-500 text-lg">💡</span>
          <div className="text-left">
            <h3 className="font-semibold text-blue-800 mb-2 text-sm sm:text-base">사용법 안내</h3>
            <ul className="text-xs sm:text-sm text-blue-700 space-y-1">
              <li>• <strong>모든 금액은 "만원" 단위</strong>로 입력하세요 (예: 300만원 → 300 입력)</li>
              <li>• 입력값 변경 시 자동으로 계산이 업데이트됩니다</li>
              <li className="hidden sm:block">• 입력 필드에 마우스를 올리면 예시를 확인할 수 있습니다</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

        {/* 입력 섹션 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 p-4 sm:p-6 lg:p-8 bg-white rounded-xl shadow-lg">

          {/* 생활비 섹션 */}
          <div className="col-span-1 sm:col-span-2 lg:col-span-4">
            <h2 className="text-lg sm:text-xl font-bold text-gray-700 mb-3 sm:mb-4 flex items-center">
              <span className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-100 rounded-full flex items-center justify-center mr-2 text-sm sm:text-base">
                💰
              </span>
              생활비 설정
            </h2>
            <div className="border-b border-gray-200 mb-3 sm:mb-4"></div>
          </div>
          <div className="flex flex-col group">
            <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1">은퇴 전 생활비 (월)</label>
            <div className="relative">
              <input
                type="number"
                name="preRetirementExpenses"
                value={inputs.preRetirementExpenses}
                onChange={handleInputChange}
                placeholder="예: 300"
                className="border-2 border-gray-200 rounded-lg p-2 sm:p-3 pr-10 sm:pr-12 bg-yellow-50 text-sm sm:text-base
                          focus:ring-2 focus:ring-blue-400 focus:border-blue-400
                         transition-all duration-200 hover:border-blue-300 w-full"
              />
              <span className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs sm:text-sm">만원</span>
            </div>
          </div>
          <div className="flex flex-col group">
            <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1">은퇴 후 생활비 (월)</label>
            <div className="relative">
              <input
                type="number"
                name="postRetirementExpenses"
                value={inputs.postRetirementExpenses}
                onChange={handleInputChange}
                placeholder="예: 250"
                className="border-2 border-gray-200 rounded-lg p-2 sm:p-3 pr-10 sm:pr-12 bg-yellow-50 text-sm sm:text-base
                          focus:ring-2 focus:ring-blue-400 focus:border-blue-400
                         transition-all duration-200 hover:border-blue-300 w-full"
              />
              <span className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs sm:text-sm">만원</span>
            </div>
          </div>
          <div className="flex flex-col group">
            <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1">물가상승률 (%)</label>
            <input
              type="number"
              name="inflationRate"
              value={inputs.inflationRate}
              onChange={handleInputChange}
              step="0.1"
              className="border-2 border-gray-200 rounded-lg p-2 sm:p-3 bg-yellow-50 text-sm sm:text-base
                         focus:ring-2 focus:ring-blue-400 focus:border-blue-400
                         transition-all duration-200 hover:border-blue-300 w-full"
            />
          </div>

          {/* 나이 섹션 */}
          <div className="col-span-1 sm:col-span-2 lg:col-span-4">
            <h2 className="text-lg sm:text-xl font-bold text-gray-700 mt-4 sm:mt-6 mb-3 sm:mb-4 flex items-center">
              <span className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-100 rounded-full flex items-center justify-center mr-2 text-sm sm:text-base">
                🕰️
              </span>
              나이 설정
            </h2>
            <div className="border-b border-gray-200 mb-3 sm:mb-4"></div>
          </div>
          <div className="flex flex-col group">
            <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1">현재 나이</label>
            <input
              type="number"
              name="currentAge"
              value={inputs.currentAge}
              onChange={handleInputChange}
              className="border-2 border-gray-200 rounded-lg p-2 sm:p-3 bg-yellow-50 text-sm sm:text-base
                         focus:ring-2 focus:ring-blue-400 focus:border-blue-400
                         transition-all duration-200 hover:border-blue-300 w-full"
            />
          </div>
          <div className="flex flex-col group">
            <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1">은퇴 나이</label>
            <input
              type="number"
              name="retirementAge"
              value={inputs.retirementAge}
              onChange={handleInputChange}
              className="border-2 border-gray-200 rounded-lg p-2 sm:p-3 bg-yellow-50 text-sm sm:text-base
                         focus:ring-2 focus:ring-blue-400 focus:border-blue-400
                         transition-all duration-200 hover:border-blue-300 w-full"
            />
          </div>

          {/* 교육비 섹션 */}
          <div className="col-span-1 sm:col-span-2 lg:col-span-4">
            <h2 className="text-lg sm:text-xl font-bold text-gray-700 mt-4 sm:mt-6 mb-3 sm:mb-4 flex items-center">
              <span className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-100 rounded-full flex items-center justify-center mr-2 text-sm sm:text-base">
                📚
              </span>
              교육비 설정
            </h2>
            <div className="border-b border-gray-200 mb-3 sm:mb-4"></div>
          </div>
          <div className="flex flex-col group">
            <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1">교육비 (월)</label>
            <div className="relative">
              <input
                type="number"
                value={educationExpense.monthlyAmount}
                onChange={(e) => setEducationExpense(prev => ({
                  ...prev,
                  monthlyAmount: parseFloat(e.target.value) || 0
                }))}
                placeholder="예: 100"
                className="border-2 border-gray-200 rounded-lg p-2 sm:p-3 pr-10 sm:pr-12 bg-yellow-50 text-sm sm:text-base
                          focus:ring-2 focus:ring-blue-400 focus:border-blue-400
                         transition-all duration-200 hover:border-blue-300 w-full"
              />
              <span className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs sm:text-sm">만원</span>
            </div>
          </div>
          <div className="flex flex-col group">
            <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1">교육비 종료 나이</label>
            <input
              type="number"
              value={educationExpense.endAge}
              onChange={(e) => setEducationExpense(prev => ({
                ...prev,
                endAge: parseFloat(e.target.value) || 0
              }))}
              className="border-2 border-gray-200 rounded-lg p-2 sm:p-3 bg-yellow-50 text-sm sm:text-base
                         focus:ring-2 focus:ring-blue-400 focus:border-blue-400
                         transition-all duration-200 hover:border-blue-300 w-full"
            />
          </div>

          {/* 자산 섹션 */}
          <div className="col-span-1 sm:col-span-2 lg:col-span-4">
            <h2 className="text-lg sm:text-xl font-bold text-gray-700 mt-4 sm:mt-6 mb-3 sm:mb-4 flex items-center">
              <span className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-100 rounded-full flex items-center justify-center mr-2 text-sm sm:text-base">
                💵
              </span>
              자산 설정
            </h2>
            <div className="border-b border-gray-200 mb-3 sm:mb-4"></div>
          </div>
          <div className="flex flex-col group">
            <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1">부동산</label>
            <div className="relative">
              <input
                type="number"
                name="realEstate"
                value={inputs.realEstate}
                onChange={handleInputChange}
                placeholder="예: 50000"
                className="border-2 border-gray-200 rounded-lg p-2 sm:p-3 pr-10 sm:pr-12 bg-yellow-50 text-sm sm:text-base
                          focus:ring-2 focus:ring-blue-400 focus:border-blue-400
                          transition-all duration-200 hover:border-blue-300 w-full"
              />
              <span className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs sm:text-sm">만원</span>
            </div>
          </div>
          <div className="flex flex-col group">
            <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1">저축성예금</label>
            <div className="relative">
              <input
                type="number"
                name="insuranceSavings"
                value={inputs.insuranceSavings}
                onChange={handleInputChange}
                placeholder="예: 3000"
                className="border-2 border-gray-200 rounded-lg p-2 sm:p-3 pr-10 sm:pr-12 bg-yellow-50 text-sm sm:text-base
                          focus:ring-2 focus:ring-blue-400 focus:border-blue-400
                          transition-all duration-200 hover:border-blue-300 w-full"
              />
              <span className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs sm:text-sm">만원</span>
            </div>
          </div>
          <div className="flex flex-col group">
            <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1">예금이자 (%)</label>
            <input
              type="number"
              name="savingsInterest"
              value={inputs.savingsInterest}
              onChange={handleInputChange}
              step="0.1"
              className="border-2 border-gray-200 rounded-lg p-2 sm:p-3 bg-yellow-50 text-sm sm:text-base
                         focus:ring-2 focus:ring-blue-400 focus:border-blue-400 
                         transition-all duration-200 hover:border-blue-300 w-full"
            />
          </div>
          <div className="flex flex-col group">
            <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1">국내주식</label>
            <div className="relative">
              <input
                type="number"
                name="domesticStocks"
                value={inputs.domesticStocks}
                onChange={handleInputChange}
                placeholder="예: 2000"
                className="border-2 border-gray-200 rounded-lg p-2 sm:p-3 pr-10 sm:pr-12 bg-yellow-50 text-sm sm:text-base
                          focus:ring-2 focus:ring-blue-400 focus:border-blue-400
                          transition-all duration-200 hover:border-blue-300 w-full"
              />
              <span className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs sm:text-sm">만원</span>
            </div>
          </div>
          <div className="flex flex-col group">
            <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1">해외주식</label>
            <div className="relative">
              <input
                type="number"
                name="foreignStocks"
                value={inputs.foreignStocks}
                onChange={handleInputChange}
                placeholder="예: 500"
                className="border-2 border-gray-200 rounded-lg p-2 sm:p-3 pr-10 sm:pr-12 bg-yellow-50 text-sm sm:text-base
                          focus:ring-2 focus:ring-blue-400 focus:border-blue-400
                          transition-all duration-200 hover:border-blue-300 w-full"
              />
              <span className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs sm:text-sm">만원</span>
            </div>
          </div>
          <div className="flex flex-col group">
            <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1">배당금 (월)</label>
            <div className="relative">
              <input
                type="number"
                name="dividend"
                value={inputs.dividend}
                onChange={handleInputChange}
                placeholder="예: 20"
                className="border-2 border-gray-200 rounded-lg p-2 sm:p-3 pr-10 sm:pr-12 bg-yellow-50 text-sm sm:text-base
                          focus:ring-2 focus:ring-blue-400 focus:border-blue-400
                          transition-all duration-200 hover:border-blue-300 w-full"
              />
              <span className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs sm:text-sm">만원</span>
            </div>
          </div>
          <div className="flex flex-col group">
            <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1">주식 성장률 (%)</label>
            <input
              type="number"
              name="annualReturn"
              value={inputs.annualReturn}
              onChange={handleInputChange}
              step="0.1"
              className="border-2 border-gray-200 rounded-lg p-2 sm:p-3 bg-yellow-50 text-sm sm:text-base
                         focus:ring-2 focus:ring-blue-400 focus:border-blue-400 
                         transition-all duration-200 hover:border-blue-300 w-full"
            />
          </div>

          {/* 수입 섹션 */}
          <div className="col-span-1 sm:col-span-2 lg:col-span-4">
            <h2 className="text-lg sm:text-xl font-bold text-gray-700 mt-4 sm:mt-6 mb-3 sm:mb-4 flex items-center">
              <span className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-100 rounded-full flex items-center justify-center mr-2 text-sm sm:text-base">
                💼
              </span>
              수입 설정
            </h2>
            <div className="border-b border-gray-200 mb-3 sm:mb-4"></div>
          </div>
          <div className="flex flex-col group">
            <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1">급여 수입 (연)</label>
            <div className="relative">
              <input
                type="number"
                name="annualIncome"
                value={inputs.annualIncome}
                onChange={handleInputChange}
                placeholder="예: 5000"
                className="border-2 border-gray-200 rounded-lg p-2 sm:p-3 pr-10 sm:pr-12 bg-yellow-50 text-sm sm:text-base
                         focus:ring-2 focus:ring-blue-400 focus:border-blue-400
                         transition-all duration-200 hover:border-blue-300 w-full"
              />
              <span className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs sm:text-sm">만원</span>
            </div>
          </div>
          <div className="flex flex-col group">
            <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1">퇴직/개인연금 (월)</label>
            <div className="relative">
              <input
                type="number"
                name="privatePension"
                value={inputs.privatePension}
                onChange={handleInputChange}
                placeholder="예: 150"
                className="border-2 border-gray-200 rounded-lg p-2 sm:p-3 pr-10 sm:pr-12 bg-yellow-50 text-sm sm:text-base
                         focus:ring-2 focus:ring-blue-400 focus:border-blue-400
                         transition-all duration-200 hover:border-blue-300 w-full"
              />
              <span className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs sm:text-sm">만원</span>
            </div>
          </div>
          <div className="flex flex-col group">
            <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1">퇴직/개인연금 수령 나이</label>
            <input
              type="number"
              name="privatePensionStartAge"
              value={inputs.privatePensionStartAge}
              onChange={handleInputChange}
              className="border-2 border-gray-200 rounded-lg p-2 sm:p-3 bg-yellow-50 text-sm sm:text-base
                         focus:ring-2 focus:ring-blue-400 focus:border-blue-400
                         transition-all duration-200 hover:border-blue-300 w-full"
            />
          </div>
          <div className="flex flex-col group">
            <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1">국민연금 (월)</label>
            <div className="relative">
              <input
                type="number"
                name="nationalPension"
                value={inputs.nationalPension}
                onChange={handleInputChange}
                placeholder="예: 100"
                className="border-2 border-gray-200 rounded-lg p-2 sm:p-3 pr-10 sm:pr-12 bg-yellow-50 text-sm sm:text-base
                         focus:ring-2 focus:ring-blue-400 focus:border-blue-400
                         transition-all duration-200 hover:border-blue-300 w-full"
              />
              <span className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs sm:text-sm">만원</span>
            </div>
          </div>
          <div className="flex flex-col group">
            <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1">주택연금 (월)</label>
            <div className="relative">
              <input
                type="number"
                name="housingPension"
                value={inputs.housingPension}
                onChange={handleInputChange}
                placeholder="예: 200"
                className="border-2 border-gray-200 rounded-lg p-2 sm:p-3 pr-10 sm:pr-12 bg-yellow-50 text-sm sm:text-base
                         focus:ring-2 focus:ring-blue-400 focus:border-blue-400
                         transition-all duration-200 hover:border-blue-300 w-full"
              />
              <span className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs sm:text-sm">만원</span>
            </div>
          </div>
          <div className="flex flex-col group">
            <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1">주택연금 수령 나이</label>
            <input
              type="number"
              name="housingPensionStartAge"
              value={inputs.housingPensionStartAge}
              onChange={handleInputChange}
              className="border-2 border-gray-200 rounded-lg p-2 sm:p-3 bg-yellow-50 text-sm sm:text-base
                         focus:ring-2 focus:ring-blue-400 focus:border-blue-400
                         transition-all duration-200 hover:border-blue-300 w-full"
            />
          </div>
          <div className="flex flex-col group">
            <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1">부수입 (월)</label>
            <div className="relative">
              <input
                type="number"
                name="extraIncome"
                value={inputs.extraIncome}
                onChange={handleInputChange}
                placeholder="예: 50"
                className="border-2 border-gray-200 rounded-lg p-2 sm:p-3 pr-10 sm:pr-12 bg-yellow-50 text-sm sm:text-base
                         focus:ring-2 focus:ring-blue-400 focus:border-blue-400
                         transition-all duration-200 hover:border-blue-300 w-full"
              />
              <span className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs sm:text-sm">만원</span>
            </div>
          </div>
        </div>

        {/* 경고/성공 메시지 */}
        {warning && (
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border-l-4 border-red-500 rounded-r-xl shadow-md transition-all duration-300">
            <div className="flex items-center">
              <svg
                className="h-5 w-5 sm:h-6 sm:w-6 text-red-500 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <span className="text-red-700 font-semibold text-sm sm:text-base">{warning}</span>
            </div>
          </div>
        )}
        {successMessage && (
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-green-100 text-green-700 rounded-lg border border-green-400 text-sm sm:text-base">
            {successMessage}
          </div>
        )}

    {/* 테이블 섹션 - 모바일 최적화 */}
    <div className="w-full mx-auto">
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        {/* 뷰 모드 선택 버튼 */}
        <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-800">연도별 계획</h3>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">보기 형식:</span>
            <div className="flex bg-white rounded-lg p-1 shadow-sm border">
              <button
                onClick={() => setViewMode('card')}
                className={`px-3 py-1 text-xs rounded transition-colors ${
                  viewMode === 'card' 
                    ? 'bg-blue-500 text-white' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                카드
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-1 text-xs rounded transition-colors ${
                  viewMode === 'table' 
                    ? 'bg-blue-500 text-white' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                테이블
              </button>
            </div>
          </div>
        </div>

        {/* 카드 형태로 표시 */}
        <div className={`${viewMode === 'card' ? 'block' : 'hidden'}`}>
          <div className="max-h-96 overflow-y-auto">
            {planData.map((row, index) => (
              <div
                key={row.age}
                className={`p-4 border-b border-gray-100 ${
                  index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                } ${
                  row.totalBalance <= 0 ? 'border-l-4 border-l-red-500 bg-red-50' : ''
                }`}
              >
                <div className="flex justify-between items-center mb-2">
                  <span className="font-bold text-lg text-blue-600">{row.age}세</span>
                  <span className={`font-semibold ${row.totalBalance > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    총자산: {formatAmount(row.totalBalance)}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-gray-600">생활비:</span>
                    <span className="ml-1 font-medium">{formatAmount(row.monthlyExpenses)}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">급여:</span>
                    <span className="ml-1 font-medium">{formatAmount(row.salary)}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">연금:</span>
                    <span className="ml-1 font-medium">{formatAmount(row.pension)}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">현금흐름:</span>
                    <span className={`ml-1 font-medium ${row.monthlyCashFlow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatAmount(row.monthlyCashFlow)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 테이블 표시 */}
        <div className={`${viewMode === 'table' ? 'block' : 'hidden'} overflow-x-auto`}>
          <table className="table-auto w-full text-xs sm:text-sm min-w-max">
            <thead>
              <tr className="bg-gradient-to-r from-blue-50 to-indigo-50">
                <th className="p-2 sm:p-3 text-left font-semibold text-gray-800 whitespace-nowrap min-w-[50px]">나이</th>
                <th className="p-2 sm:p-3 text-right font-semibold text-gray-700 whitespace-nowrap min-w-[80px]">생활비</th>
                <th className="p-2 sm:p-3 text-right font-semibold text-gray-700 whitespace-nowrap min-w-[70px]">교육비</th>
                <th className="p-2 sm:p-3 text-right font-semibold text-gray-700 whitespace-nowrap min-w-[60px]">기타</th>
                <th className="p-2 sm:p-3 text-right font-semibold text-gray-700 whitespace-nowrap min-w-[80px]">지출합계</th>
                <th className="p-2 sm:p-3 text-right font-semibold text-gray-700 whitespace-nowrap min-w-[80px]">급여수입</th>
                <th className="p-2 sm:p-3 text-right font-semibold text-gray-700 whitespace-nowrap min-w-[80px]">연금수입</th>
                <th className="p-2 sm:p-3 text-right font-semibold text-gray-700 whitespace-nowrap min-w-[70px]">부수입</th>
                <th className="p-2 sm:p-3 text-right font-semibold text-gray-700 whitespace-nowrap min-w-[70px]">부동산</th>
                <th className="p-2 sm:p-3 text-right font-semibold text-gray-700 whitespace-nowrap min-w-[90px]">저축성예금</th>
                <th className="p-2 sm:p-3 text-right font-semibold text-gray-700 whitespace-nowrap min-w-[80px]">국내주식</th>
                <th className="p-2 sm:p-3 text-right font-semibold text-gray-700 whitespace-nowrap min-w-[80px]">해외주식</th>
                <th className="p-2 sm:p-3 text-right font-semibold text-gray-700 whitespace-nowrap min-w-[70px]">배당금</th>
                <th className="p-2 sm:p-3 text-right font-semibold text-gray-700 whitespace-nowrap min-w-[90px]">총자산</th>
                <th className="p-2 sm:p-3 text-right font-semibold text-gray-700 whitespace-nowrap min-w-[90px]">연현금흐름</th>
              </tr>
            </thead>
            <tbody>
              {planData.map((row, index) => (
                <tr
                  key={row.age}
                  className={`border-b hover:bg-gray-50 transition-colors duration-150 ${
                    index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                  } ${
                    row.totalBalance <= 0 ? 'border-l-4 border-l-red-500 bg-red-50' : ''
                  }`}
                >
                  <td className="p-2 sm:p-3 whitespace-nowrap">{row.age}</td>
                  <td className="p-2 sm:p-3 text-right whitespace-nowrap">{formatAmount(row.monthlyExpenses)}</td>
                  <td className="p-2 sm:p-3 text-right whitespace-nowrap">{formatAmount(row.educationExpenses)}</td>
                  <td className="p-2 sm:p-3 text-right whitespace-nowrap">{formatAmount(row.otherExpenses)}</td>
                  <td className="p-2 sm:p-3 text-right whitespace-nowrap">{formatAmount(row.totalExpenses)}</td>
                  <td className="p-2 sm:p-3 text-right whitespace-nowrap">{formatAmount(row.salary)}</td>
                  <td className="p-2 sm:p-3 text-right whitespace-nowrap">{formatAmount(row.pension)}</td>
                  <td className="p-2 sm:p-3 text-right whitespace-nowrap">{formatAmount(row.extraIncome)}</td>
                  <td className="p-2 sm:p-3 text-right whitespace-nowrap">{formatAmount(row.realEstate)}</td>
                  <td className="p-2 sm:p-3 text-right whitespace-nowrap">{formatAmount(row.savingsBalance)}</td>
                  <td className="p-2 sm:p-3 text-right whitespace-nowrap">{formatAmount(row.domesticStocks)}</td>
                  <td className="p-2 sm:p-3 text-right whitespace-nowrap">{formatAmount(row.foreignStocks)}</td>
                  <td className="p-2 sm:p-3 text-right whitespace-nowrap">{formatAmount(row.dividend)}</td>
                  <td className="p-2 sm:p-3 text-right whitespace-nowrap">{formatAmount(row.totalBalance)}</td>
                  <td className="p-2 sm:p-3 text-right whitespace-nowrap">{formatAmount(row.monthlyCashFlow)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</div>

  );
};

export default RetirementPlanner;
